import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import staffApi from '../../staffApi';
import { allTimezones } from '../../../../utils/timezones';

const CreateStudent = ({ onCreateStudent }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tutors, setTutors] = useState([]);
  const [magicLink, setMagicLink] = useState(null);
  // Helper function to get date 6 months from now
  const getDefaultExpiryDate = () => {
    const today = new Date();
    const sixMonthsFromNow = new Date(today);
    sixMonthsFromNow.setMonth(today.getMonth() + 6);
    return sixMonthsFromNow.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  const [formData, setFormData] = useState({
    // User fields
    first_name: '',
    last_name: '',
    email: '',
    phone_no: '',
    // Student fields
    gender: '',
    timezone: '',
    city: '',
    state: '',
    country: '',
    // Lesson pack fields
    subject: '',
    no_of_hours: '',
    currency: 'USD',
    price: '',
    expiry_date: getDefaultExpiryDate(), // Set default to 6 months from now
    notes: '',
    // Optional fields
    assignedTutor: ''
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const availableSubjects = [
    'Mathematics',
    'Physics',
    'Chemistry',
    'Biology',
    'Computer Science',
    'Statistics',
    'Economics',
    'English',
    'History',
    'Geography'
  ];

  const genderChoices = [
    { value: 'MALE', label: 'Male' },
    { value: 'FEMALE', label: 'Female' },
    { value: 'OTHERS', label: 'Others' }
  ];

  const currencyChoices = [
    { value: 'USD', label: 'US Dollar' },
    { value: 'CAD', label: 'Canadian Dollar' }
  ];

  // Use all pytz timezones
  const commonTimezones = allTimezones;

  useEffect(() => {
    fetchTutors();
  }, []);

  const fetchTutors = async () => {
    try {
      const response = await staffApi.getTutors();
      if (response.success) {
        setTutors(response.data.tutors || []);
      }
    } catch (error) {
      console.error('Error fetching tutors:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };


  const validateForm = () => {
    const newErrors = {};

    // User fields validation
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone_no.trim()) {
      newErrors.phone_no = 'Phone number is required';
    }

    // Student fields validation
    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    if (!formData.timezone) {
      newErrors.timezone = 'Timezone is required';
    }

    // Lesson pack fields validation
    if (!formData.subject) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.no_of_hours || formData.no_of_hours === '' || Number(formData.no_of_hours) <= 0) {
      newErrors.no_of_hours = 'Number of hours is required and must be greater than 0';
    }

    if (!formData.price || formData.price === '' || Number(formData.price) <= 0) {
      newErrors.price = 'Price is required and must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form data before validation:', formData);
    
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    console.log('Form validation passed, submitting data');
    setLoading(true);
    setSuccessMessage('');

    try {
      // Convert assignedTutor name to tutor_id
      const submitData = { ...formData };
      if (formData.assignedTutor) {
        const selectedTutor = tutors.find(tutor => tutor.name === formData.assignedTutor);
        if (selectedTutor) {
          submitData.tutor_id = selectedTutor.id;
        }
      }
      // Remove assignedTutor from submit data as backend expects tutor_id
      delete submitData.assignedTutor;
      
      console.log('Creating student with data:', submitData);
      const result = await onCreateStudent(submitData);
      console.log('Student creation result:', result);
      if (result.success) {
        setSuccessMessage(result.message);
        // Store magic link if provided
        if (result.data && result.data.magic_link) {
          setMagicLink(result.data.magic_link);
        }
        // Reset form
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          phone_no: '',
          gender: '',
          timezone: '',
          city: '',
          state: '',
          country: '',
          subject: '',
          no_of_hours: '',
          currency: 'USD',
          price: '',
          expiry_date: getDefaultExpiryDate(), // Reset to 6 months from now
          notes: '',
          assignedTutor: ''
        });
        // Navigate back after a short delay
        setTimeout(() => {
          navigate('/staff/students');
        }, 2000);
      } else {
        setErrors({ submit: result.message });
      }
    } catch (error) {
      setErrors({ submit: 'An error occurred while creating the student' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        {/* <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Students Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Create, view, and manage student information and assignments.
          </p>
        </div> */}
        <button
          onClick={() => navigate('/staff/students')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Students
        </button>
        <h2 className="mt-4 text-xl font-semibold text-gray-900">Create New Student</h2>
        <p className="mt-1 text-sm text-gray-600">
          Add a new student to the system with their information and assignments.
        </p>
      </div>

      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Success!</h3>
              <div className="mt-2 text-sm text-green-700">{successMessage}</div>
            </div>
          </div>
        </div>
      )}

      {magicLink && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Magic Link Generated!</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p className="mb-2">Student can access their dashboard using this magic link:</p>
                <div className="bg-white p-3 rounded border">
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={magicLink.url}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(magicLink.url);
                        alert('Magic link copied to clipboard!');
                      }}
                      className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                    >
                      Copy
                    </button>
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-600">
                  Expires: {new Date(magicLink.expires_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 bg-white shadow rounded-lg p-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                First Name *
              </label>
              <input
                type="text"
                name="first_name"
                id="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  errors.first_name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter first name"
              />
              {errors.first_name && <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>}
            </div>

            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                Last Name *
              </label>
              <input
                type="text"
                name="last_name"
                id="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  errors.last_name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter last name"
              />
              {errors.last_name && <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter email address"
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="phone_no" className="block text-sm font-medium text-gray-700">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone_no"
                id="phone_no"
                value={formData.phone_no}
                onChange={handleInputChange}
                className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  errors.phone_no ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter phone number"
              />
              {errors.phone_no && <p className="mt-1 text-sm text-red-600">{errors.phone_no}</p>}
            </div>

            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                Gender *
              </label>
              <select
                name="gender"
                id="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  errors.gender ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select gender</option>
                {genderChoices.map(choice => (
                  <option key={choice.value} value={choice.value}>{choice.label}</option>
                ))}
              </select>
              {errors.gender && <p className="mt-1 text-sm text-red-600">{errors.gender}</p>}
            </div>

            <div>
              <label htmlFor="timezone" className="block text-sm font-medium text-gray-700">
                Timezone *
              </label>
              <select
                name="timezone"
                id="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  errors.timezone ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select timezone</option>
                {commonTimezones.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
              {errors.timezone && <p className="mt-1 text-sm text-red-600">{errors.timezone}</p>}
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Location Information (Optional)</h3>
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                City
              </label>
              <input
                type="text"
                name="city"
                id="city"
                value={formData.city}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Enter city"
              />
            </div>

            <div>
              <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                State/Province
              </label>
              <input
                type="text"
                name="state"
                id="state"
                value={formData.state}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Enter state/province"
              />
            </div>

            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                Country
              </label>
              <input
                type="text"
                name="country"
                id="country"
                value={formData.country}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Enter country"
              />
            </div>
          </div>
        </div>

        {/* Lesson Pack Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Lesson Pack Information</h3>
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                Subject *
              </label>
              <select
                name="subject"
                id="subject"
                value={formData.subject}
                onChange={handleInputChange}
                className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  errors.subject ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select a subject</option>
                {availableSubjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
              {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject}</p>}
            </div>

            <div>
              <label htmlFor="no_of_hours" className="block text-sm font-medium text-gray-700">
                Number of Hours *
              </label>
              <input
                type="number"
                name="no_of_hours"
                id="no_of_hours"
                min="1"
                value={formData.no_of_hours}
                onChange={handleInputChange}
                className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  errors.no_of_hours ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter number of hours"
              />
              {errors.no_of_hours && <p className="mt-1 text-sm text-red-600">{errors.no_of_hours}</p>}
            </div>

            <div>
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700">
                Currency *
              </label>
              <select
                name="currency"
                id="currency"
                value={formData.currency}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                {currencyChoices.map(choice => (
                  <option key={choice.value} value={choice.value}>{choice.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                Price *
              </label>
              <input
                type="number"
                name="price"
                id="price"
                min="0"
                step="0.01"
                value={formData.price}
                onChange={handleInputChange}
                className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  errors.price ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter price"
              />
              {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price}</p>}
            </div>

            <div>
              <label htmlFor="expiry_date" className="block text-sm font-medium text-gray-700">
                Expiry Date (Default: 6 months from now)
              </label>
              <input
                type="date"
                name="expiry_date"
                id="expiry_date"
                value={formData.expiry_date}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="assignedTutor" className="block text-sm font-medium text-gray-700">
                Assign Tutor (Optional)
              </label>
              <select
                name="assignedTutor"
                id="assignedTutor"
                value={formData.assignedTutor}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">Select a tutor (optional)</option>
                {tutors.map(tutor => (
                  <option key={tutor.id} value={tutor.name}>{tutor.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
              Notes (Optional)
            </label>
            <textarea
              name="notes"
              id="notes"
              rows={3}
              value={formData.notes}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Enter any additional notes about the student or lesson pack"
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/staff/students')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Student'}
          </button>
        </div>

        {errors.submit && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{errors.submit}</div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default CreateStudent;
